/**
 * ridgevale-global.js
 * Core site interactivity. Progressive enhancement — all critical content
 * is accessible without JS. Interactions layer on top.
 *
 * Modules:
 *  1. Header — scroll behaviour, transparent → solid transition
 *  2. Navigation — desktop dropdown (click-to-open), mobile menu + accordion
 *  3. Cart — drawer open/close, genuinely async add/update via Section Rendering API
 *  4. Search Drawer — open/close, focus management
 *  5. Anchor links — resilient in-page scrolling that survives theme duplication
 *  6. Product — variant selection, gallery
 *  7. Utilities — throttle, trap focus, money formatting
 *
 * Coding principle: Explain WHY, not WHAT.
 * PRD Reference: Section 49 — Micro-Interaction Standards, Section 56 — Cart
 */

/* ── UTILITIES ───────────────────────────────── */

/**
 * Throttle to animation frames. Used for scroll handlers: a debounce would
 * only fire once scrolling STOPS, which leaves the header visibly stale
 * during a continuous scroll.
 */
function rafThrottle(fn) {
  let queued = false;
  return (...args) => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      fn(...args);
    });
  };
}

/**
 * Trap keyboard focus within an element (e.g. open drawer).
 * Returns a cleanup function to remove the listener.
 */
function trapFocus(element) {
  function handler(e) {
    if (e.key !== 'Tab') return;
    // Queried on each keypress rather than cached, because the cart drawer's
    // contents are re-rendered after every update.
    const focusable = element.querySelectorAll(
      'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  element.addEventListener('keydown', handler);
  return () => element.removeEventListener('keydown', handler);
}

/**
 * Announce a message to screen readers via an off-screen live region.
 */
function announce(message, priority = 'polite') {
  let region = document.getElementById('a11y-announcer');
  if (!region) {
    region = document.createElement('div');
    region.id = 'a11y-announcer';
    region.setAttribute('aria-live', priority);
    region.setAttribute('aria-atomic', 'true');
    region.className = 'visually-hidden';
    document.body.appendChild(region);
  }
  region.textContent = '';
  requestAnimationFrame(() => { region.textContent = message; });
}

/**
 * Root path for the current market/locale. Hardcoding "/cart" breaks
 * customers browsing a market subfolder, so all Ajax URLs are built
 * from this value.
 */
function rootUrl() {
  const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
  return root || '/';
}

/** Format Shopify cents using the active presentment currency. */
function formatMoney(cents) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency
    }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

/* ── 1. HEADER ─────────────────────────────────── */

(function initHeader() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const canGoTransparent = header.classList.contains('is-transparent');
  const SCROLL_THRESHOLD = 80; // px before the header becomes fully opaque

  function updateHeader() {
    if (!canGoTransparent) return;
    const scrolled = window.scrollY > SCROLL_THRESHOLD;
    header.classList.toggle('is-scrolled', scrolled);
    header.classList.toggle('is-transparent', !scrolled);
  }

  window.addEventListener('scroll', rafThrottle(updateHeader), { passive: true });
  updateHeader();
})();

/* ── 2. NAVIGATION ─────────────────────────────── */

(function initNavigation() {

  // ── Desktop Dropdowns ────────────────────────────────
  // Click-to-open, not hover. Opening the menu is a deliberate action:
  // hover menus open when the cursor merely passes over the header, and
  // behave inconsistently on touch/hybrid devices. The trigger is a real
  // link, so keyboard Enter fires the same click handler and non-JS
  // visitors still navigate somewhere useful; with JS the click becomes
  // a toggle and the destination stays reachable via the dropdown's own
  // links (e.g. "All Equipment").
  const navItems = document.querySelectorAll('[data-nav-item]');

  function closeAllDropdowns(except) {
    navItems.forEach(item => {
      if (item === except) return;
      item.classList.remove('is-open');
      const t = item.querySelector('[data-nav-trigger]');
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  navItems.forEach(item => {
    const trigger = item.querySelector('[data-nav-trigger]');
    const dropdown = item.querySelector('.site-nav__dropdown');
    if (!trigger || !dropdown) return;

    trigger.addEventListener('click', e => {
      e.preventDefault();
      const isOpen = item.classList.contains('is-open');
      closeAllDropdowns(item);
      item.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });

    // Tabbing out of the item (e.g. onto the next nav link) closes it,
    // so keyboard users never leave an orphaned dropdown open.
    item.addEventListener('focusout', e => {
      if (!item.contains(e.relatedTarget)) {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  });

  // Clicking anywhere outside the nav dismisses an open dropdown.
  document.addEventListener('click', e => {
    if (e.target.closest('[data-nav-item]')) return;
    closeAllDropdowns();
  });

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    closeAllDropdowns();
  });

  // ── Mobile Menu ──────────────────────────────────────
  const toggleBtn = document.querySelector('[data-mobile-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const iconMenu = document.querySelector('[data-icon-menu]');
  const iconClose = document.querySelector('[data-icon-close]');
  let focusTrap = null;

  if (!toggleBtn || !mobileNav) return;

  function collapseMobileSubMenus() {
    mobileNav.querySelectorAll('[data-mobile-sub-toggle][aria-expanded="true"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
      const wrap = document.getElementById(btn.getAttribute('aria-controls'));
      if (wrap) wrap.classList.remove('is-open');
    });
  }

  function openMobileNav() {
    toggleBtn.setAttribute('aria-expanded', 'true');
    mobileNav.setAttribute('aria-hidden', 'false');
    mobileNav.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (iconMenu) iconMenu.style.display = 'none';
    if (iconClose) iconClose.style.display = '';
    focusTrap = trapFocus(mobileNav);
    const firstLink = mobileNav.querySelector('a, button');
    if (firstLink) firstLink.focus();
  }

  function closeMobileNav() {
    toggleBtn.setAttribute('aria-expanded', 'false');
    mobileNav.setAttribute('aria-hidden', 'true');
    mobileNav.classList.remove('is-open');
    document.body.style.overflow = '';
    if (iconMenu) iconMenu.style.display = '';
    if (iconClose) iconClose.style.display = 'none';
    if (focusTrap) { focusTrap(); focusTrap = null; }
    // Collapse any open sub-menus so the drawer reopens in a predictable,
    // compact state next time.
    collapseMobileSubMenus();
    toggleBtn.focus();
  }

  toggleBtn.addEventListener('click', () => {
    if (mobileNav.classList.contains('is-open')) { closeMobileNav(); } else { openMobileNav(); }
  });

  // ── Mobile sub-menu accordion ────────────────────────────
  // The JS only flips state (aria-expanded + is-open); the easing itself
  // lives in CSS (grid-row 0fr→1fr + fade), so motion stays on the
  // design-system timing tokens and respects prefers-reduced-motion.
  // Delegated from the nav element so it keeps working if the drawer
  // markup is ever re-rendered.
  mobileNav.addEventListener('click', e => {
    const toggle = e.target.closest('[data-mobile-sub-toggle]');
    if (!toggle) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    const wrap = document.getElementById(toggle.getAttribute('aria-controls'));
    if (wrap) wrap.classList.toggle('is-open', !expanded);
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && mobileNav.classList.contains('is-open')) closeMobileNav();
  });
})();

/* ── 3. CART ──────────────────────────────────
   Every handler here is delegated from `document` rather than bound to the
   drawer element, because the drawer's markup is replaced wholesale after
   each update via the Section Rendering API. Element-bound listeners would
   be destroyed by that swap. ── */

(function initCart() {
  const SECTION_ID = 'cart-drawer';
  const SECTION_SELECTOR = '#shopify-section-' + SECTION_ID;

  let focusTrap = null;
  let busy = false;

  const drawer = () => document.querySelector('[data-cart-drawer]');
  const overlay = () => document.querySelector('[data-cart-overlay]');

  function isOpen() {
    const d = drawer();
    return !!d && d.classList.contains('is-open');
  }

  function applyOpenState(open) {
    const d = drawer();
    const o = overlay();
    if (!d) return;
    d.classList.toggle('is-open', open);
    d.setAttribute('aria-hidden', open ? 'false' : 'true');
    if (o) {
      o.classList.toggle('is-open', open);
      o.setAttribute('aria-hidden', open ? 'false' : 'true');
    }
    document.body.style.overflow = open ? 'hidden' : '';
  }

  function openCart() {
    if (!drawer()) return;
    applyOpenState(true);
    if (focusTrap) focusTrap();
    focusTrap = trapFocus(drawer());
    const first = drawer().querySelector('button, a');
    if (first) first.focus();
  }

  function closeCart() {
    if (!drawer()) return;
    applyOpenState(false);
    if (focusTrap) { focusTrap(); focusTrap = null; }
    const toggle = document.querySelector('[data-cart-toggle]');
    if (toggle) toggle.focus();
  }

  /** Update the header bubble without re-rendering the header section. */
  function setCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.classList.toggle('visually-hidden', count === 0);
    });
  }

  /**
   * Re-render only the cart drawer section. This replaces the previous
   * implementation, which called window.location.reload() on every single
   * quantity change — PRD Section 56 requires these updates to be async.
   */
  async function refreshCart(reopen) {
    const res = await fetch(rootUrl() + '?sections=' + SECTION_ID);
    const data = await res.json();
    const parsed = new DOMParser().parseFromString(data[SECTION_ID], 'text/html');
    const fresh = parsed.querySelector(SECTION_SELECTOR);
    const target = document.querySelector(SECTION_SELECTOR);
    if (fresh && target) target.innerHTML = fresh.innerHTML;

    const cartRes = await fetch(rootUrl() + 'cart.js');
    const cart = await cartRes.json();
    setCartCount(cart.item_count);

    if (reopen) applyOpenState(true);
    return cart;
  }

  async function changeLine(key, quantity) {
    if (busy) return;
    busy = true;
    try {
      const res = await fetch(rootUrl() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      if (!res.ok) throw new Error('change failed');
      const cart = await refreshCart(isOpen());
      announce('Cart updated. ' + cart.item_count + ' item' + (cart.item_count !== 1 ? 's' : '') + ' in your cart.');
    } catch (e) {
      announce('We could not update your cart. Please try again.', 'assertive');
    } finally {
      busy = false;
    }
  }

  // ── Delegated clicks ─────────────────────────────────
  document.addEventListener('click', e => {
    if (e.target.closest('[data-cart-toggle]')) {
      e.preventDefault();
      openCart();
      return;
    }

    if (e.target.closest('[data-cart-close]') || e.target.closest('[data-cart-overlay]')) {
      // The empty-state button is both a close trigger and a real link;
      // let the navigation happen rather than swallowing the click.
      const link = e.target.closest('a[href]');
      if (!link) e.preventDefault();
      closeCart();
      return;
    }

    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const key = qtyBtn.dataset.qtyChange;
      const delta = parseInt(qtyBtn.dataset.qtyDelta, 10);
      const row = document.querySelector('[data-cart-item="' + key + '"]');
      const valueEl = row && row.querySelector('.cart-item__qty-value');
      const current = parseInt(valueEl && valueEl.textContent, 10) || 0;
      changeLine(key, Math.max(0, current + delta));
      return;
    }

    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      e.preventDefault();
      changeLine(removeBtn.dataset.removeItem, 0);
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && isOpen()) closeCart();
  });

  // ── Async add to cart ────────────────────────────────
  // Without this the form does a full navigation to /cart, which is the
  // wrong destination when the theme is configured to use a drawer.
  document.addEventListener('submit', async e => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form || !drawer()) return;

    e.preventDefault();
    if (busy) return;
    busy = true;

    const submitBtn = form.querySelector('[type="submit"]');
    const originalLabel = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.textContent = 'Adding…';
    }

    try {
      const res = await fetch(rootUrl() + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const payload = await res.json();

      if (!res.ok) {
        announce(payload.description || 'This item could not be added to your cart.', 'assertive');
        return;
      }

      await refreshCart(false);
      openCart();
      announce('Added to your cart.');
    } catch (err) {
      announce('We could not add this item. Please try again.', 'assertive');
    } finally {
      if (submitBtn) {
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = originalLabel;
      }
      busy = false;
    }
  });
})();

/* ── 4. SEARCH DRAWER ───────────────────────────────── */

(function initSearchDrawer() {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  const input = drawer.querySelector('[data-search-input]');
  let focusTrap = null;
  let lastTrigger = null;

  function openSearch() {
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    focusTrap = trapFocus(drawer);
    if (input) input.focus();
  }

  function closeSearch() {
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    if (focusTrap) { focusTrap(); focusTrap = null; }
    if (lastTrigger) lastTrigger.focus();
  }

  document.addEventListener('click', e => {
    const toggle = e.target.closest('[data-search-toggle]');
    if (toggle) {
      e.preventDefault();
      lastTrigger = toggle;
      openSearch();
      return;
    }
    if (e.target.closest('[data-search-close]') || e.target.closest('.search-drawer__backdrop')) {
      e.preventDefault();
      closeSearch();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeSearch();
  });
})();

/* ── 5. ANCHOR LINKS ─────────────────────────────────
   Shopify regenerates section IDs (template--<id>__<key>) whenever a theme
   is duplicated, so any CTA storing a raw section ID silently breaks on the
   next duplicate. Aliases below resolve to a section's schema `class`, which
   is stable across duplication. Add new aliases here rather than pasting
   generated IDs into theme settings. ── */

(function initAnchors() {
  const ALIASES = {
    '#basecamp': '.section-basecamp-grid',
    '#stories': '.section-basecamp-stories',
    '#featured': '.section-featured-products',
    '#newsletter': '.section-newsletter'
  };

  function resolve(hash) {
    if (ALIASES[hash]) return document.querySelector(ALIASES[hash]);
    try {
      return document.querySelector(hash);
    } catch (e) {
      return null;
    }
  }

  function scrollToTarget(target) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    // Move focus for keyboard and screen reader users, without adding a
    // permanent tabstop to the page.
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  document.addEventListener('click', e => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;
    const target = resolve(hash);
    if (!target) return;
    e.preventDefault();
    scrollToTarget(target);
  });
})();

/* ── 6. PRODUCT ──────────────────────────────── */

(function initProduct() {
  const form = document.querySelector('[data-product-form]');
  const idInput = document.querySelector('[data-variant-id]');
  const optionButtons = document.querySelectorAll('.variant-btn[data-option]');

  // ── Gallery ─────────────────────────────────────────
  // image_tag emits a srcset alongside src. The gallery's inline handler
  // only swaps src, and srcset wins in the browser's source selection —
  // so without clearing it the main image often would not change at all.
  const mainImage = document.getElementById('gallery-main-image');
  document.querySelectorAll('[data-gallery-thumb]').forEach(thumb => {
    thumb.addEventListener('click', () => {
      if (mainImage) mainImage.removeAttribute('srcset');
    });
  });

  // ── Variant selection ─────────────────────────────────
  if (!form || !idInput || !optionButtons.length) return;

  const handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (!handleMatch) return;

  let product = null;

  fetch(rootUrl() + 'products/' + handleMatch[1] + '.js')
    .then(r => r.json())
    .then(data => { product = data; })
    .catch(() => { product = null; });

  /**
   * Pre-order is decided per variant in product-main.liquid (tagged
   * product, no stock on hand) and handed over as a comma-wrapped id list,
   * because the storefront product JSON exposes `available` but not stock
   * levels — on its own it can't tell a pre-order from an in-stock option.
   */
  function isPreorderVariant(id) {
    const stockEl = document.querySelector('.product-purchase__stock');
    const ids = (stockEl && stockEl.dataset.preorderVariants) || '';
    return ids.indexOf(',' + id + ',') !== -1;
  }

  /** Read the current option selection, ordered by option position. */
  function currentSelection() {
    const selection = [];
    document.querySelectorAll('.variant-group').forEach(group => {
      const chosen = group.querySelector('.variant-btn.is-selected');
      const anyBtn = group.querySelector('.variant-btn[data-option]');
      if (!anyBtn) return;
      const position = parseInt(anyBtn.dataset.option, 10) || selection.length + 1;
      selection[position - 1] = chosen ? chosen.dataset.value : null;
    });
    return selection;
  }

  function findVariant(selection) {
    if (!product || !product.variants) return null;
    return product.variants.find(variant =>
      variant.options.every((value, index) => value === selection[index])
    ) || null;
  }

  function updateGalleryForVariant(variant) {
    if (!mainImage || !variant || !variant.featured_image || !variant.featured_image.src) return;
    mainImage.removeAttribute('srcset');
    mainImage.src = variant.featured_image.src;
  }

  function updateUI(variant) {
    const priceEl = document.querySelector('.product-info__price');
    const compareEl = document.querySelector('.product-info__compare-price');
    const submitBtn = form.querySelector('[type="submit"]');
    const stockEl = document.querySelector('.product-purchase__stock');

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Unavailable';
      }
      if (stockEl) stockEl.textContent = 'This combination is not available';
      return;
    }

    idInput.value = variant.id;

    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }

    const isPreorder = variant.available && isPreorderVariant(variant.id);

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      if (!variant.available) {
        submitBtn.textContent = 'Currently unavailable';
      } else {
        submitBtn.textContent = isPreorder ? 'Pre-Order' : 'Add to Basecamp';
      }
    }

    const preorderBadge = document.querySelector('[data-preorder-badge]');
    if (preorderBadge) preorderBadge.hidden = !isPreorder;

    if (stockEl) {
      stockEl.classList.remove(
        'product-purchase__stock--in',
        'product-purchase__stock--pre',
        'product-purchase__stock--low',
        'product-purchase__stock--out'
      );
      if (!variant.available) {
        stockEl.classList.add('product-purchase__stock--out');
        stockEl.textContent = 'Currently out of stock';
      } else if (isPreorder) {
        // Built from the section's own wording (data attributes) so the
        // message matches the first page load and stays editable.
        stockEl.classList.add('product-purchase__stock--pre');
        stockEl.textContent = (stockEl.dataset.preorderMessage || 'Pre-order') + ' ';
        if (stockEl.dataset.preorderLinkUrl) {
          const contactLink = document.createElement('a');
          contactLink.href = stockEl.dataset.preorderLinkUrl;
          contactLink.textContent = stockEl.dataset.preorderLinkText || 'Contact us';
          stockEl.appendChild(contactLink);
        }
      } else {
        stockEl.classList.add('product-purchase__stock--in');
        stockEl.textContent = 'In stock — dispatched within 2 working days';
      }
    }

    // Keep the SKU row in the specification table in step with the variant.
    document.querySelectorAll('.spec-table tr').forEach(row => {
      const label = row.querySelector('th');
      const value = row.querySelector('td');
      if (label && value && label.textContent.trim() === 'SKU') {
        value.textContent = variant.sku || '—';
      }
    });

    updateGalleryForVariant(variant);

    // Make the selection shareable and refresh-safe.
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  optionButtons.forEach(button => {
    button.addEventListener('click', e => {
      e.preventDefault();
      const group = button.closest('.variant-options');
      if (group) {
        group.querySelectorAll('.variant-btn').forEach(sibling => {
          sibling.classList.remove('is-selected');
          sibling.setAttribute('aria-pressed', 'false');
        });
      }
      button.classList.add('is-selected');
      button.setAttribute('aria-pressed', 'true');

      // Keep the "Colour: Grey" style label in step with the selection.
      const wrapper = button.closest('.variant-group');
      const labelStrong = wrapper && wrapper.querySelector('.variant-group__label strong');
      if (labelStrong) labelStrong.textContent = button.dataset.value;

      updateUI(findVariant(currentSelection()));
    });
  });
})();

/* ── 7. NOTIFY JS AVAILABLE ───────────────────────────── */
document.documentElement.classList.replace('no-js', 'js');
